import { useEffect } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';
import { ArrowLeft, ExternalLink, LogIn } from 'lucide-react';
import { Button, Tabs, TabsList, TabsTrigger } from '@staff-app/ui';
import {
  useStaffGetCustomer,
  useStaffGetCustomerInfo,
  useStaffLoginAsCustomer,
  useStaffBillingSubscriptionStatus,
  useStaffResolveCustomer,
} from '../hooks/useStaffCustomer';
import {
  accountStatus,
  parseStripeId,
  parsePaymentFlags,
  parsePairedScreens,
  parseMaxScreens,
} from '../lib/customer-info';
import { CustomerFacts } from '../components/CustomerFacts';
import { useCustomersSearchStore } from '../stores/customers-search-store';
import { useRecentCustomersStore } from '../stores/recent-customers-store';

/**
 * Container for the per-customer drill-in. Renders:
 *   - header: company name, plan badge, rollup counts
 *   - nested <Tabs> (Devices / Fleets) that syncs with URL
 *   - <Outlet /> for the selected tab page
 */
export function CustomerShell() {
  const { accountId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const { data, loading, error } = useStaffGetCustomer(accountId);
  const customer = data?.staffGetCustomer;

  // Same payment/limitation blob the customer panel + search card use — gives us
  // the Stripe id and the trial/active flags that StaffCustomer doesn't carry.
  const { data: infoData } = useStaffGetCustomerInfo(accountId ?? null);
  const info = infoData?.staffGetCustomerInfo;
  const stripeId = parseStripeId(info);
  const { isTrial, isActive } = parsePaymentFlags(info);
  const subStatus = useStaffBillingSubscriptionStatus(stripeId).data
    ?.staffBillingSubscriptionStatus;
  const status = accountStatus(isTrial, isActive);
  // What the operator typed to find this customer. In-memory and NOT in the URL, so it is
  // absent after a hard reload or when the page is opened from a link — the row then simply
  // does not render, rather than showing a blank "Searched:".
  const usersQuery = useCustomersSearchStore((st) => st.usersQuery);
  // The same resolve the search column ran (an Apollo cache hit), for the role badge. Only
  // when it resolved to THIS account — reached via "Also a team member of", the searched
  // email's role here is not what the resolve reported.
  const resolvedSearch = useStaffResolveCustomer(usersQuery).data?.staffResolveCustomer;
  const searchedRole =
    resolvedSearch?.accountId === accountId ? resolvedSearch?.inputRole : undefined;

  // Track this customer as recently accessed (drill-in / deep-link entry).
  const addRecent = useRecentCustomersStore((s) => s.addRecent);
  useEffect(() => {
    if (customer?.accountId) {
      addRecent({
        accountId: customer.accountId,
        companyName: customer.companyName,
        ownerEmail: customer.email,
      });
    }
  }, [customer?.accountId, customer?.companyName, customer?.email, addRecent]);

  const [loginMutation, { loading: loginLoading }] = useStaffLoginAsCustomer();

  const handleLogin = async () => {
    if (!accountId) return;
    try {
      const result = await loginMutation({ variables: { accountId } });
      const appUrl = result.data?.staffLoginAsCustomer?.appUrl;
      if (appUrl) window.open(appUrl, '_blank', 'noopener,noreferrer');
    } catch (e: unknown) {
      toast.error('Login failed', { description: (e as Error)?.message ?? 'Unknown error' });
    }
  };

  // Derive active tab from path.
  const activeTab = location.pathname.includes('/fleets')
    ? 'fleets'
    : location.pathname.includes('/teams')
      ? 'teams'
      : 'devices';

  const handleTabChange = (next: string) => {
    if (!accountId) return;
    navigate(`/staff/customers/${encodeURIComponent(accountId)}/${next}`, {
      replace: true,
    });
  };

  // Back to the previous Customers search (its query is in the URL, so the
  // results re-appear). Fall back to a fresh search page when there's no
  // in-app history (e.g. the drill-in was opened from a deep link).
  const handleBack = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) navigate(-1);
    else navigate('/staff/customers');
  };

  return (
    <div className="flex flex-col gap-4 p-6" data-testid="customer-shell">
      <Button
        variant="ghost"
        size="sm"
        className="w-fit cursor-pointer"
        onClick={handleBack}
        aria-label={'Back to customers'}
        data-testid="customer-shell-back"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        {'Back to customers'}
      </Button>

      {loading && !customer ? (
        <div className="text-sm text-muted-foreground">{'Loading customer…'}</div>
      ) : error ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
          {'Failed to load customer.'} {error.message}
        </div>
      ) : customer ? (
        <>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex flex-col">
              <h1 className="text-2xl font-bold text-foreground" data-testid="customer-name">
                {customer.companyName}
              </h1>
              {/*
                Same block the search card and the customer panel render, from the same
                component — so the three surfaces cannot drift again, and the ids gain copy
                buttons here too.
              */}
              <div className="mt-1 flex flex-col gap-0.5 text-xs text-muted-foreground">
                <CustomerFacts
                  prefix="customer-shell"
                  status={status}
                  plan={customer.plan}
                  planFeature={customer.planFeature}
                  ownerEmail={customer.email}
                  accountId={customer.accountId}
                  stripeId={stripeId}
                  subscriptionStatus={subStatus?.status}
                  subscriptionMultiple={subStatus?.multiple}
                  pairedScreens={parsePairedScreens(info)}
                  maxScreens={parseMaxScreens(info)}
                  searched={usersQuery ? { value: usersQuery, role: searchedRole } : null}
                />
              </div>
            </div>

            {/* Right side: same customer actions as the search panel. Login
                re-points the staff user's accountId, so it works with no owner. */}
            <div className="flex flex-wrap items-center gap-2">
              {accountId ? (
                <Button
                  onClick={handleLogin}
                  disabled={loginLoading}
                  className="cursor-pointer gap-2"
                  data-testid="customer-shell-login-btn"
                >
                  <LogIn className="h-4 w-4" />
                  {loginLoading ? 'Opening…' : 'Login as Customer'}
                  <ExternalLink className="h-3 w-3 opacity-60" />
                </Button>
              ) : null}
              {stripeId ? (
                <Button
                  variant="outline"
                  onClick={() =>
                    window.open(
                      `https://dashboard.stripe.com/customers/${stripeId}`,
                      '_blank',
                      'noopener,noreferrer',
                    )
                  }
                  className="cursor-pointer gap-2"
                  data-testid="customer-shell-stripe-btn"
                >
                  <ExternalLink className="h-4 w-4" />
                  {'Stripe'}
                </Button>
              ) : null}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
            <RollupCard label={'Total devices'} value={customer.totalDevices} />
            <RollupCard label={'Online'} value={customer.onlineDevices} tone="text-success" />
            <RollupCard label={'Stale'} value={customer.staleDevices} tone="text-warning" />
            <RollupCard
              label={'Offline'}
              value={customer.offlineDevices}
              tone="text-destructive"
            />
            {/* +1 for the implicit Default fleet — totalFleets counts only the
                customer's custom fleets; the Default fleet has no DB row. */}
            <RollupCard label={'Fleets'} value={(customer.totalFleets ?? 0) + 1} />
            {/* totalTeams already includes the Default team (a real `teams` row),
                so no +1 here. */}
            <RollupCard label={'Teams'} value={customer.totalTeams ?? 0} />
          </div>
        </>
      ) : null}

      <Tabs value={activeTab} onValueChange={handleTabChange} className="mt-2">
        <TabsList>
          <TabsTrigger value="devices" className="cursor-pointer" data-testid="tab-devices">
            {'Devices'}
          </TabsTrigger>
          <TabsTrigger value="fleets" className="cursor-pointer" data-testid="tab-fleets">
            {'Fleets'}
          </TabsTrigger>
          <TabsTrigger value="teams" className="cursor-pointer" data-testid="tab-teams">
            {'Teams'}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <Outlet />
    </div>
  );
}

function RollupCard({ label, value, tone }: { label: string; value: number; tone?: string }) {
  return (
    <div className="rounded-lg border bg-card p-4 shadow-sm" data-testid={`rollup-${label}`}>
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className={`mt-1 text-2xl font-semibold ${tone ?? 'text-foreground'}`}>{value}</div>
    </div>
  );
}
